'use client';

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { saveScrollPosition, markPendingRestore, restoreScrollIfPending } from '@/lib/scrollRestoration';

export default function ScrollManager() {
  const pathname = usePathname();

  // Continuously save scroll position for the current URL (rAF-throttled)
  useEffect(() => {
    let rafId: number | null = null;
    const onScroll = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        saveScrollPosition();
        rafId = null;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, []);

  // When the browser back/forward button is pressed, mark which URL needs restoring.
  // At the time popstate fires, window.location is already updated to the destination URL.
  useEffect(() => {
    const onPopstate = () => {
      markPendingRestore(window.location.pathname + window.location.search);
    };
    window.addEventListener('popstate', onPopstate);
    return () => window.removeEventListener('popstate', onPopstate);
  }, []);

  // On every path change, try immediate restore.
  // Works for pages with synchronous content.
  // Pages with async content (shop-catalog, home) will restore after their data loads.
  useEffect(() => {
    restoreScrollIfPending();
  }, [pathname]);

  return null;
}
